import { Dialog } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { SuperSiteTargetingNumToString } from '~/src/dash/api/console-api';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  IListViewTableColumnInfo,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { IconToggle } from '~/src/dash/components/toggles/IconToggle';
import {
  Col,
  Cols,
  Panel,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextInput,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteChannelExisting,
} from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState } from '~/src/dash/utils/loading-states';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isDemoAccount } from '~/src/dash/utils/ui';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  IChannelMappingRow,
  IChannelRow,
  IGetChannelMappingsUiParams,
  orderDirection,
} from '~/src/dash/views/channels/api';
import {
  formatDataToLVTRows,
  IChannelMappingsConfig,
} from '~/src/dash/views/channels/ChannelMappings/config';
import { useChannelsStore } from '~/src/dash/views/channels/contexts';

interface IChannelMappingsProps {
  cfg: IChannelMappingsConfig;
  updateCfg: (params: IGetChannelMappingsUiParams) => void;
  channelMappings: IChannelMappingRow[];
  fetchChannelMappings: (params: IGetChannelMappingsUiParams) => void;
  totalRows: number;
  loadState: LoadingState;
}

export const ChannelMappings = ({
  cfg,
  updateCfg,
  channelMappings,
  fetchChannelMappings,
  totalRows,
  loadState,
}: IChannelMappingsProps) => {
  const { session, user } = useBaseStore();
  const { readAuthorization, writeAuthorization } = useChannelsStore();
  const { cypressClassName, defaultState } = cfg;
  const isSuperUser = hasRoleSuperUser(user.user);

  const [order, setOrder] = useState<orderDirection>(defaultState.orderDirection);
  const [orderBy, setOrderBy] = useState<string>(defaultState.orderBy);
  const [searchInput, setSearchInput] = useState<string>('');
  const [currPage, setCurrPage] = useState<number>(defaultState.page);
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(defaultState.rowsPerPage);
  const [isTableExpanded, setIsTableExpanded] = useState<boolean>(defaultState.isTableExpanded);
  const [activeSubChannels, setActiveSubChannels] = useState<Array<Partial<IChannelRow>> | null>(
    null,
  );

  // search box
  const handleUpdateSearchInput = (text: string) => {
    track('Channel - Mappings - Search', { isSuperUser }, user.user);
    setSearchInput(text);
  };

  // this sets up the changes to force a re-sort
  const handleClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection, label } = column;
    track('Channel - Mappings - Column Header Sort', { isSuperUser, sortBy: label }, user.user);
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setCurrPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setCurrPage(0);
      });
    }
  };

  // update page number
  const handleChangePage = (_event: React.MouseEvent | null, page: number) => {
    setCurrPage(page);
  };

  // update rows per page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    track(
      'Channels - Mappings - Change Rows Per Page',
      { isSuperUser, rowsPerPage: updatedRowsPerPage },
      user.user,
    );
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setCurrPage(0);
    });
  };

  useEffect(() => {
    fetchChannelMappings({
      orderBy,
      order,
      searchInput,
      rowsPerPage,
      currPage,
    });
  }, [searchInput, currPage, rowsPerPage, order, orderBy]);

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
    >
      {/* SEARCH */}
      <Panel highlighted largeHorizontalPadding verticalPadding>
        <Rows>
          <Row style={{ padding: '8px 0px' }}>
            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
              <Col>
                <TextInput
                  enableClear
                  handleOnChange={handleUpdateSearchInput}
                  msDelay={800}
                  placeholder='Search by Name, ID'
                  prefix='search'
                  initialValue={searchInput}
                />
              </Col>
              <Col>
                <SearchResultsText
                  numRowsDisplayed={channelMappings.length}
                  numTotalRows={totalRows}
                />
              </Col>
              <Col style={{ marginLeft: 'auto' }}>
                <IconToggle
                  activeBackgroundColor='inherit'
                  backgroundColor='inherit'
                  hoverColor={publicaColors.veryLightGray}
                  imagePath='assets/shared/svg/PaddingExpand16.svg'
                  imageStyle={{ height: '28px', padding: '6px' }}
                  isActive={isTableExpanded}
                  setIsActive={(active) => {
                    setIsTableExpanded(active);
                    updateCfg({ isTableExpanded: active });
                  }}
                  tooltipPlacement='top-end'
                  tooltipText={isTableExpanded ? 'Compact Table' : 'Comfortable Table'}
                />
              </Col>
            </Cols>
          </Row>

          {/* TABLE */}
          <Row noHorizontalPadding>
            <ListViewTable
              activeColumnKeys={defaultState.activeColumnKeys || []}
              columns={cfg.table.columns}
              displayNameWithID={!isDemoAccount(user.user.Username)}
              isExpanded={isTableExpanded}
              customFunctions={{
                handleClickOnSubChannels: (SubChannels: Partial<IChannelRow>[]) =>
                  setActiveSubChannels(SubChannels),
              }}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleOnClickColumnHeader={handleClickColumnHeader}
              getItemSettingsRoute={(ID: string) => {
                return (
                  getRoutePath(RouteChannelExisting, extractRouteParams({ session, user }), {
                    ch: ID,
                  }) + '?tab=mapping'
                );
              }}
              isReadAuthorized={readAuthorization.isAuthorized}
              isSuperUser={isSuperUser}
              isWriteAuthorized={writeAuthorization.isAuthorized}
              loadState={loadState}
              order={order}
              orderBy={orderBy}
              page={currPage}
              rows={formatDataToLVTRows(channelMappings, cfg.table)}
              rowsPerPage={rowsPerPage}
              totalRowCount={totalRows}
              className={cypressClassName}
            />
          </Row>
        </Rows>
      </Panel>

      {/* SUB CHANNELS MODAL */}
      {activeSubChannels && (
        <Dialog
          fullWidth
          maxWidth='md'
          keepMounted
          open={!!activeSubChannels}
          onClose={() => setActiveSubChannels(null)}
        >
          <ModalSelectTable
            title='Sub Channels'
            singularTitle='Channel'
            items={activeSubChannels}
            onSelectRow={(ID: string) => {
              navigate(RouteChannelExisting, extractRouteParams({ session, user }), { ch: ID });
            }}
            interactive={true}
            customColumns={[
              {
                title: 'Targeting Keys',
                field: 'SuperSiteTargeting',
                searchable: true,
                render: (row: any) => <Text small>{row.SuperSiteTargeting}</Text>,
              },
              {
                title: 'Targeting Endpoint Param',
                field: 'SuperSiteTargetingType',
                searchable: true,
                render: (row: any) => (
                  <Text small>{SuperSiteTargetingNumToString[row.SuperSiteTargetingType]}</Text>
                ),
              },
            ]}
          />
        </Dialog>
      )}
    </Panels>
  );
};
